import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { AuthProvider, canAccessAdmin, canBackupRead, canCampaignsRead, canCasesAuthor, canCasesRead, canInventoryRead, canManageMasters, canProcure, canQC, canRental, canSalesRead, canSalesWrite, canSystemMaintenance, canVendorReturnsRead, canVendorReturnsWrite, canWarrantyRead, useAuth } from "@/lib/auth";
import LoginPage from "@/pages/LoginPage";
import AdminShell from "@/components/AdminShell";
import GlobalSearchModal from "@/components/GlobalSearchModal";
import CallQueuePage from "@/pages/CallQueuePage";
import DashboardPage from "@/pages/DashboardPage";
import EligibilityRulesPage from "@/pages/EligibilityRulesPage";
import PricingRulesPage from "@/pages/PricingRulesPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import TelephonyProvidersPage from "@/pages/TelephonyProvidersPage";
import VendorsPage from "@/pages/VendorsPage";
import PurchasesPage from "@/pages/PurchasesPage";
import NewPurchasePage from "@/pages/NewPurchasePage";
import PurchaseDetailPage from "@/pages/PurchaseDetailPage";
import PurchaseQCJobPage from "@/pages/PurchaseQCJobPage";
import DigitalPassportPage from "@/pages/DigitalPassportPage";
import UnitReceiveQcPage from "@/pages/UnitReceiveQcPage";
import QCJobsPage from "@/pages/QCJobsPage";
import QCJobPage from "@/pages/QCJobPage";
import InventoryPage from "@/pages/InventoryPage";
import InventoryDetailPage from "@/pages/InventoryDetailPage";
import ConfigureLaptopPage from "@/pages/ConfigureLaptopPage";
import SalesPage from "@/pages/SalesPage";
import NewSalesPage from "@/pages/NewSalesPage";
import SalesDetailPage from "@/pages/SalesDetailPage";
import BackupPage from "@/pages/BackupPage";
import SystemMaintenancePage from "@/pages/SystemMaintenancePage";
import CasesPage from "@/pages/CasesPage";
import NewCasePage from "@/pages/NewCasePage";
import CaseDetailPage from "@/pages/CaseDetailPage";
import CasesV2Page from "@/pages/CasesV2Page";
import NewCaseV2Page from "@/pages/NewCaseV2Page";
import CaseV2DetailPage from "@/pages/CaseV2DetailPage";
import CaseV2PrintLabelPage from "@/pages/CaseV2PrintLabelPage";
import CaseV2PrintReceiptPage from "@/pages/CaseV2PrintReceiptPage";
import WarrantiesPage from "@/pages/WarrantiesPage";
import WarrantyDetailPage from "@/pages/WarrantyDetailPage";
import CampaignsPage from "@/pages/CampaignsPage";
import CampaignDetailPage from "@/pages/CampaignDetailPage";
import VendorReturnsPage from "@/pages/VendorReturnsPage";
import NewVendorReturnPage from "@/pages/NewVendorReturnPage";
import VendorReturnDetailPage from "@/pages/VendorReturnDetailPage";
import B2BCataloguePage from "@/b2b/B2BCataloguePage";
import B2BLotDetailPage from "@/b2b/B2BLotDetailPage";
import B2BAdminPage from "@/b2b/B2BAdminPage";
import RentalPortalPage from "@/rental/RentalPortalPage";
import RentalAdminPage from "@/rental/RentalAdminPage";
import "@/App.css";

function RequireAuth({ children, gate = canAccessAdmin }) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (!gate(user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600">
        Your role does not have access to this page.
      </div>
    );
  }
  return children;
}

export default function App() {
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
      </AuthProvider>
    </BrowserRouter>
  );
}

// The B2B catalogue is served on the b2b.* subdomain at the root, and also
// under /b2b/* in the shared preview app for testing (see b2bApi.b2bBase).
const IS_B2B_HOST = typeof window !== "undefined" && window.location.hostname.startsWith("b2b.");

function AppRoutes({ searchOpen, setSearchOpen }) {
  if (IS_B2B_HOST) {
    return (
      <Routes>
        <Route path="/" element={<B2BCataloguePage />} />
        <Route path="/lot/:lotNumber" element={<B2BLotDetailPage />} />
        <Route path="/admin" element={<B2BAdminPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  return (
    <>
      <Routes>
        {/* B2B module (preview path-based access; production uses b2b.* subdomain) */}
        <Route path="/b2b" element={<B2BCataloguePage />} />
        <Route path="/b2b/lot/:lotNumber" element={<B2BLotDetailPage />} />
        <Route path="/b2b/admin" element={<B2BAdminPage />} />

        {/* Rental customer portal — isolated rental-party auth (own login) */}
        <Route path="/rental" element={<RentalPortalPage />} />

        <Route path="/login" element={<LoginPage />} />
          <Route
            path="/admin"
            element={<RequireAuth><AdminShell onOpenSearch={() => setSearchOpen(true)} /></RequireAuth>}
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="purchases" element={<PurchasesPage />} />
            <Route path="purchases/new" element={<RequireAuth gate={canProcure}><NewPurchasePage /></RequireAuth>} />
            <Route path="purchases/:id" element={<PurchaseDetailPage />} />
            <Route path="purchases/:id/unit-flow" element={<UnitReceiveQcPage />} />
            <Route path="purchases/:purchase_id/qc-job" element={<RequireAuth gate={canQC}><PurchaseQCJobPage /></RequireAuth>} />
            <Route path="passports/:passport_id" element={<RequireAuth gate={canInventoryRead}><DigitalPassportPage /></RequireAuth>} />
            <Route path="vendors" element={<VendorsPage />} />
            <Route path="qc/jobs" element={<RequireAuth gate={canQC}><QCJobsPage /></RequireAuth>} />
            <Route path="qc/jobs/:passport_id" element={<RequireAuth gate={canQC}><QCJobPage /></RequireAuth>} />
            <Route path="inventory" element={<RequireAuth gate={canInventoryRead}><InventoryPage /></RequireAuth>} />
            <Route path="inventory/:passport_id" element={<RequireAuth gate={canInventoryRead}><InventoryDetailPage /></RequireAuth>} />
            <Route path="configure-laptop" element={<RequireAuth gate={canInventoryRead}><ConfigureLaptopPage /></RequireAuth>} />
            <Route path="vendor-returns" element={<RequireAuth gate={canVendorReturnsRead}><VendorReturnsPage /></RequireAuth>} />
            <Route path="rental" element={<RequireAuth gate={canRental}><RentalAdminPage /></RequireAuth>} />
            <Route path="vendor-returns/new" element={<RequireAuth gate={canVendorReturnsWrite}><NewVendorReturnPage /></RequireAuth>} />
            <Route path="vendor-returns/:vr_id" element={<RequireAuth gate={canVendorReturnsRead}><VendorReturnDetailPage /></RequireAuth>} />
            <Route path="sales" element={<RequireAuth gate={canSalesRead}><SalesPage /></RequireAuth>} />
            <Route path="sales/new" element={<RequireAuth gate={canSalesWrite}><NewSalesPage /></RequireAuth>} />
            <Route path="sales/:so_id" element={<RequireAuth gate={canSalesRead}><SalesDetailPage /></RequireAuth>} />
            <Route path="cases" element={<RequireAuth gate={canCasesRead}><CasesV2Page /></RequireAuth>} />
            <Route path="cases/new" element={<RequireAuth gate={canCasesAuthor}><NewCaseV2Page /></RequireAuth>} />
            <Route path="cases/:case_id" element={<RequireAuth gate={canCasesRead}><CaseV2DetailPage /></RequireAuth>} />
            <Route path="cases-legacy" element={<RequireAuth gate={canCasesRead}><CasesPage /></RequireAuth>} />
            <Route path="cases-legacy/new" element={<RequireAuth gate={canCasesAuthor}><NewCasePage /></RequireAuth>} />
            <Route path="cases-legacy/:case_id" element={<RequireAuth gate={canCasesRead}><CaseDetailPage /></RequireAuth>} />
            <Route path="warranties" element={<RequireAuth gate={canWarrantyRead}><WarrantiesPage /></RequireAuth>} />
            <Route path="warranties/:warranty_id" element={<RequireAuth gate={canWarrantyRead}><WarrantyDetailPage /></RequireAuth>} />
            <Route path="campaigns" element={<RequireAuth gate={canCampaignsRead}><CampaignsPage /></RequireAuth>} />
            <Route path="campaigns/:campaign_id" element={<RequireAuth gate={canCampaignsRead}><CampaignDetailPage /></RequireAuth>} />
            <Route path="reports" element={<RequireAuth gate={canSalesRead}><ReportsPage /></RequireAuth>} />
            <Route path="settings" element={<RequireAuth gate={canManageMasters}><SettingsPage /></RequireAuth>} />
            <Route path="backups" element={<RequireAuth gate={canBackupRead}><BackupPage /></RequireAuth>} />
            <Route path="system-maintenance" element={<RequireAuth gate={canSystemMaintenance}><SystemMaintenancePage /></RequireAuth>} />
            <Route path="call-queue" element={<RequireAuth gate={canManageMasters}><CallQueuePage /></RequireAuth>} />
            <Route path="warranty-eligibility-rules" element={<RequireAuth gate={canManageMasters}><EligibilityRulesPage /></RequireAuth>} />
            <Route path="warranty-pricing-rules" element={<RequireAuth gate={canManageMasters}><PricingRulesPage /></RequireAuth>} />
            <Route path="telephony-providers" element={<RequireAuth gate={canManageMasters}><TelephonyProvidersPage /></RequireAuth>} />
          </Route>
          <Route path="/print/label/:case_id" element={<RequireAuth gate={canCasesRead}><CaseV2PrintLabelPage /></RequireAuth>} />
          <Route path="/print/receipt/:case_id" element={<RequireAuth gate={canCasesRead}><CaseV2PrintReceiptPage /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
        <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      </>
  );
}
